/**
 * Created by neo on 11.01.2026
 * Create Post Modal - Create community wall posts with text and media (images/videos)
 */
import * as React from 'react';
import { useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from '@emotion/styled';
import { useToast } from '../../../Hooks/useToast';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRootStore } from '../../../Store/useRootStore';
import { CommunityWallPost, createCommunityWallPost } from '../../../Model/Engagement/CommunityWall/CommunityWallPost';
import { Button } from '../../../Components/Button';
import { MultiMediaUploadInput, UploadedMedia } from '../../../Components/MultiMediaUploadInput';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { Theme } from '../../../Theme/Theme';

export type CreatePostModalProps = {
  isOpen: boolean;
  onClose: () => void;
  challengeId: string;
  locationId?: string;
  onPostCreated?: (post: CommunityWallPost) => void;
};

const StyledModal = styled(Modal)`
  .modal-content {
    border-radius: 16px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${Theme.Colors.warmGrey};
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 14px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${Theme.Colors.primary};
  }

  &:disabled {
    background-color: ${Theme.Colors.lightestGrey};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${Theme.Colors.warmGrey};
  }
`;

const CharCounter = styled.div<{ $isNearLimit: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.$isNearLimit ? Theme.Colors.error : Theme.Colors.warmGrey)};
  text-align: right;
  margin-top: 8px;
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.error};
  margin: 8px 0 0 0;
`;

const MAX_CHARS = 500;
const MAX_MEDIA = 3;

export const CreatePostModal: React.FC<CreatePostModalProps> = observer(
  ({ isOpen, onClose, challengeId, locationId, onPostCreated }) => {
    const { t } = useTrans();
    const toast = useToast();

    const [text, setText] = useState('');
    const [media, setMedia] = useState<UploadedMedia[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isNearLimit = text.length > MAX_CHARS * 0.9;
    const isOverLimit = text.length > MAX_CHARS;
    const canSubmit = !isSubmitting && !isOverLimit && (text.trim().length > 0 || media.length > 0);

    const handleClose = useCallback(() => {
      if (!isSubmitting) {
        setText('');
        setMedia([]);
        setError(null);
        onClose();
      }
    }, [isSubmitting, onClose]);

    const handleSubmit = useCallback(async () => {
      if (!canSubmit) return;

      setIsSubmitting(true);
      setError(null);

      try {
        const formData = new FormData();

        if (text.trim()) {
          formData.append('text', text.trim());
        }

        formData.append('challengeId', challengeId);
        if (locationId) {
          formData.append('locationId', locationId);
        }

        media.forEach((item, index) => {
          formData.append(`media[${index}]`, item.file);
        });

        const response = await HttpBackend.postForm('/coach-program/engagement/community-wall', formData);

        toast.showToast(t('post.create.success', { defaultValue: 'Post created successfully!' }), 'success');

        if (onPostCreated && response) {
          const createdPost = createCommunityWallPost(response);
          onPostCreated(createdPost);
        }

        handleClose();
      } catch (err) {
        console.error('Error creating post:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        toast.showToast(t('post.create.error', { defaultValue: 'Failed to create post. Please try again.' }), 'error');
      } finally {
        setIsSubmitting(false);
      }
    }, [canSubmit, text, challengeId, locationId, media, toast, t, onPostCreated, handleClose]);

    return (
      <StyledModal isOpen={isOpen} toggle={handleClose} size="md" centered>
        <ModalHeader toggle={handleClose}>{t('post.create.title', { defaultValue: 'Create Post' })}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label htmlFor="postText">{t('communityWall.yourMessage', { defaultValue: 'Your Message' })}</Label>
            <TextArea
              id="postText"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('communityWall.whatToShare', {
                defaultValue: "What's your story? Share your experience...",
              })}
              disabled={isSubmitting}
              maxLength={MAX_CHARS + 50}
            />
            <CharCounter $isNearLimit={isNearLimit}>
              {text.length} / {MAX_CHARS}
            </CharCounter>
            {isOverLimit && <ErrorText>{t('post.create.error.tooLong', { defaultValue: 'Text is too long' })}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>{t('communityWall.addMedia', { defaultValue: 'Photos & Videos' })}</Label>
            <MultiMediaUploadInput media={media} onChange={setMedia} maxMedia={MAX_MEDIA} disabled={isSubmitting} />
          </FormGroup>

          {error && <ErrorText>{error}</ErrorText>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={isSubmitting}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting
              ? t('communityWall.saving', { defaultValue: 'Posting...' })
              : t('communityWall.sharePost', { defaultValue: 'Share Post' })}
          </Button>
        </ModalFooter>
      </StyledModal>
    );
  },
);
